import AgentAPI from "agent-api";
import { createContext, useContext, useEffect, useState } from "react";
import { getAlgorithm } from "../lib/util";

const accountContext = createContext();

const KEY_ID = "id_key_id";
const KEY_PASSWORD_LOCAL_NAME = "KeyPassword";
const KEY_PASSWORD_NAMESPACE = "http://www.w3.org/2001/XMLSchema";

const LOGIN_ERROR_MESSAGS = {
  invalid: "Invalid password or username.",
  tryAgainLater: "Try again later.",
};

async function hash(str) {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export const Currency = async () => {
  try {
    const token = window.sessionStorage.getItem("AgentAPI.Token") || null;
    if (!token) return null;
    const res = await AgentAPI.Wallet.GetBalance();
    return res.currency;
  } catch {
    return null;
  }
};

export function AccountProvider({ children }) {
  const [isLoggingIn, setLoggingIn] = useState(false);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState(undefined);
  const [jwt, setJwt] = useState(undefined);
  const [isQuickLoggedIn, setQuickLoggedIn] = useState(false);
  const [sessionAuth, setSessionAuth] = useState(false);
  const [idCreateState, setIdCreateState] = useState({}); // a state to preserve data between screens when creating an account

  ///////////////////////////////////////////////////
  /////////////////////////////////////////////////// Credentials
  ///////////////////////////////////////////////////

  const createKey = async (str) => {
    const algo = await getAlgorithm();
    const accountPassword = getPassword();
    if (!accountPassword) {
      console.warn("[accountContext] Cannot create key: no account password stored");
      return null;
    }
    // Ensure we hash a string; fall back to account password if input is missing
    let source = typeof str === "string" && str.length > 0 ? str : accountPassword;
    // Add randomness to avoid reusing the same key password
    source = `${source}:${Date.now()}:${Math.random()}`;
    const keyPassword = await hash(source, 8);
    await AgentAPI.Crypto.CreateKey(
      algo.localName,
      algo.namespace,
      KEY_ID,
      keyPassword,
      accountPassword
    );
    return keyPassword;
  };

  const saveKeyPassword = async (password) => {
    await AgentAPI.Storage.SavePrivateXml(
      `<${KEY_PASSWORD_LOCAL_NAME} xmlns="${KEY_PASSWORD_NAMESPACE}">${password}</${KEY_PASSWORD_LOCAL_NAME}>`
    );
  };

  const getKeyPassword = async () => {
    try {
      const res = await AgentAPI.Storage.LoadPrivateXml(
        KEY_PASSWORD_LOCAL_NAME,
        KEY_PASSWORD_NAMESPACE
      );
      return res.Xml.value;
    } catch (err) {
      if (err && err.statusCode === 404) {
        console.warn("[accountContext] Key password XML not found; will create a new key");
        return null;
      }
      throw err;
    }
  };

  const storePassword = (password) => {
    sessionStorage.setItem("agent-user-password", password);
  };

  const getPassword = () => {
    return sessionStorage.getItem("agent-user-password");
  };


  const storeUsername = (Username) => {
    sessionStorage.setItem("agent-user-Username", Username);
  };

  const getUsername = () => {
    return sessionStorage.getItem("agent-user-Username");
  };



  ///////////////////////////////////////////////////
  /////////////////////////////////////////////////// Legal
  ///////////////////////////////////////////////////

  const [legalId, setLegalId] = useState(); // make it more accuret
  const [legalIdState, setLegalIdState] = useState(() => {
    try {
      return window.sessionStorage.getItem("agent-legal-id-state") || null;
    } catch {
      return null;
    }
  });
  const [legalStatus, setLegalStatus] = useState({
    pendingApplication: undefined,
  });

  // Hydrate auth flags from storage on initial load/navigation
  useEffect(() => {
    try {
      const ssAuth = window.sessionStorage.getItem('sessionAuth') === 'true';
      const ssQuick = window.sessionStorage.getItem('isQuickLoggedIn') === 'true';
      const ssLogged = window.sessionStorage.getItem('isLoggedIn') === 'true';
      if (ssAuth || ssQuick || ssLogged) {
        if (ssAuth) setSessionAuth(true);
        if (ssQuick) setQuickLoggedIn(true);
        if (ssLogged) setLoggedIn(true);
      }
      const storedLegal = window.localStorage.getItem('agent-legal-id') || window.sessionStorage.getItem('agent-legal-id');
      if (storedLegal && !legalId) {
        setLegalId(storedLegal);
      }
      const storedLegalState = window.sessionStorage.getItem("agent-legal-id-state");
      if (storedLegalState) setLegalIdState(storedLegalState);
      else if (storedLegal) {
        const signatureRaw = window.localStorage.getItem("signatureRecived") || window.localStorage.getItem("signatureReceived");
        const signatureState = signatureRaw ? JSON.parse(signatureRaw)?.State : null;
        if (signatureState) setLegalIdState(String(signatureState));
      }
      const storedUser = window.sessionStorage.getItem('username');
      if (storedUser) setUsername(storedUser);
    } catch {
      // ignore
    }
  }, []);

  const getLegalId = async () => {
    // Require JWT before hitting AgentAPI to avoid 401s in session-auth only
    if ((!isLoggedIn && !isQuickLoggedIn) || !jwt) return;
    const res = await AgentAPI.Legal.GetIdentities(0, 1);
    const identityList = Array.isArray(res?.Identities) ? res.Identities : [];
    const approvedIdentity = identityList.find((identity) =>
      String(identity?.status?.state || "").trim().toLowerCase() === "approved"
    );
    const identity = approvedIdentity || identityList[0] || null;
    const state = String(identity?.status?.state || "NotSubmitted");

    setLegalStatus({
      pendingApplication: identityList.some((item) =>
        String(item?.status?.state || "").trim().toLowerCase() === "created"
      ),
    });
    setLegalIdState(state);
    try { window.sessionStorage.setItem("agent-legal-id-state", state); } catch { /* ignore */ }

    if (approvedIdentity) setLegalId(approvedIdentity);
    else setLegalId(undefined);

    const firstName = identity?.property?.find((value) => value?.name === "FIRST")?.value;
    if (firstName) {
      setUsername(firstName);
      storeUsername(firstName);
    }

    if (approvedIdentity) return approvedIdentity.id;

  };

  const hasLegalId = async () => {
    if (!isLoggedIn || !jwt) return false;
    const legalId = await getLegalId();
    return !!legalId;
  };

  // sign a contract with the currnet users id
  const signContract = async (contract, role) => {
    console.log("[accountContext] signContract called", { role, contractId: contract.id });
    // Avoid signing without JWT
    if (!jwt) {
      console.warn("[accountContext] Cannot sign: no JWT present.");
      return false;
    }
    const legalId = await getLegalId();

    if (!legalId) {
      console.warn("could not sign contract: no legal id");
      return;
    }

    const accountPassword = getPassword();
    if (!accountPassword) {
      console.warn("[accountContext] Cannot sign: no account password stored. Please log in with password.");
      return false;
    }

    let keyPassword = await getKeyPassword();
    if (!keyPassword) {
      console.debug("creating new key");
      const newKeyPassword = await createKey(accountPassword);
      await saveKeyPassword(newKeyPassword);
      keyPassword = newKeyPassword;
    }

    console.log("[accountContext] Preparing to sign", {
      keyId: KEY_ID,
      legalId: legalId?.id || legalId,
      role,
    });
    const result = await contract.sign(
      KEY_ID,
      keyPassword,
      accountPassword,
      legalId,
      role
    );
    console.log("[accountContext] contract.sign completed", { ok: result, role, contractId: contract.id });
    // update the balance in 10 seconds
    setTimeout(() => {
      updateBalance();
    }, 10000);

    return result;
  };

  const applyForId = async (first, last, addr, pnr, zip, city, country) => {
    if (!isLoggedIn || !jwt) {
      console.error("Not logged in");
      return;
    }
    const accountPassword = getPassword();
    if (!accountPassword) {
      console.error("No password");
      return;
    }

    const keyPassword = await createKey(accountPassword);
    await saveKeyPassword(keyPassword);
    const algo = await getAlgorithm();
    await AgentAPI.Legal.ApplyId(
      algo.localName,
      algo.namespace,
      KEY_ID,
      keyPassword,
      accountPassword,
      {
        FIRST: first,
        LAST: last,
        PNR: pnr,
        ADDR: addr,
        ZIP: zip,
        CITY: city,
        COUNTRY: country,
      }
    );
  };

  ///////////////////////////////////////////////////
  /////////////////////////////////////////////////// Account
  ///////////////////////////////////////////////////

  const login = async (username, password) => {
    try {
      try { window.sessionStorage.removeItem("agent-user-email"); } catch { /* ignore */ }
      try {
        window.localStorage.removeItem("signatureRecived");
        window.localStorage.removeItem("signatureReceived");
      } catch { /* ignore */ }
      setLegalIdState(null);
      try { window.sessionStorage.removeItem("agent-legal-id-state"); } catch { /* ignore */ }
      const response = await AgentAPI.Account.Login(username, password, 3600);
      console.log(response);
      let str = response.jwt;
      str = str.replace(/\\/g, "");
      setJwt(str);
      window.localStorage.removeItem("jwt");
      window.localStorage.setItem("jwt", str);
      storePassword(password);
      storeUsername(username);
      setUsername(username);
      setLoggedIn(true);
      getLegalId(); // to update legal id if user already have one
    } catch (err) {
      console.log(err);
      const status = err.statusCode;
      setLoggedIn(false);
      if (status === 403) {
        throw new Error(LOGIN_ERROR_MESSAGS.invalid);
      } else {
        throw new Error(LOGIN_ERROR_MESSAGS.tryAgainLater);
      }
    }
  };

  const logout = () => {
    // Reset all auth flags
    setLoggedIn(false);
    setQuickLoggedIn(false);
    setSessionAuth(false);
    setJwt(undefined);
    setUsername(undefined);
    setLegalId(undefined);
    setLegalIdState(null);

    // Clear persisted storage flags
    try {
      window.sessionStorage.removeItem("agent-user-Username");
      window.sessionStorage.removeItem("agent-user-email");
      window.sessionStorage.removeItem("agent-user-password");
      window.sessionStorage.removeItem("AgentAPI.RefreshTimerElapses");
      window.sessionStorage.removeItem("AgentAPI.Seconds");
      window.sessionStorage.removeItem("AgentAPI.RefreshTimer");
      window.sessionStorage.removeItem("AgentAPI.RefreshTimerExpires");
      window.sessionStorage.removeItem("AgentAPI.Token");
      window.sessionStorage.removeItem("sessionAuth");
      window.sessionStorage.removeItem("isQuickLoggedIn");
      window.sessionStorage.removeItem("isLoggedIn");
      window.sessionStorage.removeItem("username");
      window.sessionStorage.removeItem("agent-legal-id");
      window.sessionStorage.removeItem("agent-legal-id-state");
      window.localStorage.removeItem("agent-legal-id");
      window.localStorage.removeItem("signatureRecived");
      window.localStorage.removeItem("signatureReceived");
      window.localStorage.removeItem("jwt");
    } catch {
       // ignore  
    }
  };

  // TOOD make less jank
  const signUp = async (username, email, password, reCaptchaToken) => {
    const formData = new URLSearchParams();
    formData.set("UserName", username);
    formData.set("EMail", email);
    formData.set("Password", password);
    formData.set("RedirectionUrl", "/");
    formData.set("g-recaptcha-response", reCaptchaToken);
    formData.set("Seconds", 3600);

    const res = await fetch("/Agent/Account/CreateWebForm", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData.toString(),
    });
    if (res.status === 200 || res.status === 503 || res.status == 405) {
      storePassword(password);
      storeUsername(username);
      await AgentAPI.Account.GetSessionToken();

      startVerificationStage(email);
      return true;
    }

    return false;
  };

  //////////////////
  ////////////////// Account verification stage
  //////////////////

  const inVerificationStage = () => {
    return window.localStorage.getItem("agent-verification-email") !== null;
  };

  const startVerificationStage = (email) => {
    window.localStorage.setItem("agent-verification-email", email);
  };

  const verifyEmail = async (code) => {
    const res = await AgentAPI.Account.VerifyEMail(
      window.localStorage.getItem("agent-verification-email"),
      code
    );

    // was succesfull
    if (res.eMail) {
      window.localStorage.removeItem("agent-verification-email");
      setLoggedIn(true);
    }
  };

  ///////////////////////////////////////////////////
  /////////////////////////////////////////////////// Wallet
  ///////////////////////////////////////////////////

  const [balance, setBalance] = useState({
    amount: null,
    currency: null,
  });

  // updated the sored edaler balance
  const updateBalance = async () => {
    // Require authenticated session with JWT before calling AgentAPI
    if (!isLoggedIn || !jwt) return;
    const token = window.sessionStorage.getItem("AgentAPI.Token");
    if (!token) return;

    try {
      const response = await AgentAPI.Wallet.GetBalance();
      setBalance(response);
    } catch (err) {
      // Avoid noisy logs for 401 when token missing/expired
      if (err?.statusCode !== 401) console.error(err);
    }
  };

  // is currently logged in?
  const refreshTimerExpires =
    Number(window.sessionStorage.getItem("AgentAPI.RefreshTimerExpires")) *
    1000 || 0;

  // if tou have session data
  if (!isLoggedIn && refreshTimerExpires > Date.now()) {
    (async () => {
      // if the account is verified/enabled
      if (!inVerificationStage()) setLoggedIn(true);
    })();
  }

  // test for legal id if you get logged in
  useEffect(() => {
    if (isLoggedIn) {
      getLegalId(); // test for legal id
      updateBalance(); // update balance
    }
  }, [isLoggedIn]);


  // hydrate username from session storage after any login type
  useEffect(() => {
    if (isLoggedIn || isQuickLoggedIn) {
      const stored = getUsername();
      if (stored && stored !== username) setUsername(stored);

      // Backwards-compatible hydration for flows that persisted under a different key.
      try {
        const legacy = window.sessionStorage.getItem('username');
        if (legacy && legacy !== username) setUsername(legacy);
      } catch {
        // ignore
      }
    }
  }, [isLoggedIn, isQuickLoggedIn]);


  return (
    <accountContext.Provider
      value={{
        // account
        isLoggedIn,
        setLoggedIn,
        login,
        logout,
        signUp,
        username,
        setUsername,
        jwt,
        setJwt,
        inVerificationStage,
        verifyEmail,
        isQuickLoggedIn,
        setQuickLoggedIn,
        sessionAuth,
        setSessionAuth,

        // login popup
        setLoggingIn,
        isLoggingIn,

        // creation state (temp)
        idCreateState,
        setIdCreateState,

        // legal
        hasLegalId,
        legalId,
        legalIdState,
        setLegalIdState,
        legalStatus,
        setLegalId,
        signContract,
        applyForId,

        updateBalance,
        balance,
      }}
    >
      {children}
    </accountContext.Provider>
  );
}

export function useAccount() {
  return useContext(accountContext);
}

export default accountContext;
